import { Configuration, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import _ from "lodash";
import { IUserMSAL } from "../users";
import { beginRecovery, isDeadGrantError } from "./signInRecovery";

/**
 * User flows and custom policies of the current B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
let b2cPolicies = {
    clientId: "",
    authorities: {
        signUpSignIn: {
            authority: "",
            scopes: [] as string[],
        },
        forgotPassword: {
            authority: "",
            scopes: [] as string[],
        },
        editProfile: {
            authority: "",
            scopes: [] as string[],
        },
    },
    readScope: "",
    authorityDomain: "",
};
type IB2cPolicies = typeof b2cPolicies;

export const getB2cPolicies = () => b2cPolicies;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const createMsalConfig = (policies: IB2cPolicies): Configuration => ({
    auth: {
        clientId: policies.clientId, // This is the ONLY mandatory field that you need to supply.
        authority: policies.authorities.signUpSignIn.authority, // Use a sign-up/sign-in user-flow as a default authority
        knownAuthorities: [policies.authorityDomain.toLowerCase()], // Mark your B2C tenant's domain as trusted.
        redirectUri: "/", // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
        postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
        claimsBasedCachingEnabled: true,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        // console.info(message);
                        return;
                    case LogLevel.Verbose:
                        // console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                }
            },
        },
        tokenRenewalOffsetSeconds: 300,

    },
});

let msalInstance: PublicClientApplication = null;
export const getMsalInstance = () => msalInstance;

export const ACTIVE_ACCOUNT_STORAGE_KEY = "OPER8_ACTIVE_ACCOUNT_ID";

const isValidAccount = (account: IUserMSAL | null | undefined): account is IUserMSAL => {
    if (!account) return false;

    const expired = (account.idTokenClaims?.exp * 1000 || 0) < Date.now();
    if (expired) return false;

    const missInfos = !account.idTokenClaims?.oid;
    return !missInfos;
};

const getAccountStorageId = (account: IUserMSAL | null | undefined) => (
    account?.homeAccountId ?? account?.localAccountId ?? account?.username ?? null
);

const getStoredActiveAccountId = () => {
    try {
        return localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
    } catch {
        return null;
    }
};

const storeActiveAccountId = (account: IUserMSAL | null | undefined) => {
    try {
        const accountId = getAccountStorageId(account);
        if (accountId) localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, accountId);
        else localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
    } catch {
        // NOOP
    }
};

export const setActiveAccount = (account: IUserMSAL | null | undefined) => {
    getMsalInstance()?.setActiveAccount(account ?? null);
    storeActiveAccountId(account);
};

export const getActiveAccount = () => {
    const activeAccount = getMsalInstance()?.getActiveAccount() as IUserMSAL | null;
    if (isValidAccount(activeAccount)) {
        storeActiveAccountId(activeAccount);
        return activeAccount;
    }

    const accounts = _.filter(getMsalInstance()?.getAllAccounts() ?? [], isValidAccount) as IUserMSAL[];
    const storedActiveAccountId = getStoredActiveAccountId();
    if (storedActiveAccountId) {
        const storedActiveAccount = _.find(accounts, (account) => getAccountStorageId(account) == storedActiveAccountId);
        if (storedActiveAccount) {
            setActiveAccount(storedActiveAccount);
            return storedActiveAccount;
        }
    }

    if (accounts.length == 1) {
        setActiveAccount(accounts[0]);
        return accounts[0];
    }

    return null;
};

/**
 * A silent renewal or a sign-in response failed because the cached grant is dead (see signInRecovery.ts): forgets the cached sign-in and starts a clean one.
 * Returns false, doing nothing, when the error is something else or when a recovery already ran lately: the caller then lets the error show instead of looping.
 * When it starts the sign-in the page leaves, so the returned promise settles only if the redirect fails.
 */
export const recoverSignIn = async (error: unknown, redirectUri: string): Promise<boolean> => {
    if (!msalInstance || !isDeadGrantError(error) || !beginRecovery(sessionStorage, Date.now())) return false;

    try {
        await msalInstance.clearCache();
    } catch {
        // The clean sign-in below replaces the dead grant anyway.
    }
    storeActiveAccountId(null);
    await msalInstance.loginRedirect({ scopes: [b2cPolicies.readScope], redirectUri });
    return true;
};

export const initMsalConfig = async () => {
    let isMsalInit = false;
    while (!isMsalInit) {
        try {
            const response = await fetch("/api/v1/msal");
            const serverB2cPolicies = await response.json();
            b2cPolicies = serverB2cPolicies;
            msalInstance = new PublicClientApplication(createMsalConfig(b2cPolicies));
            await msalInstance.initialize();
            isMsalInit = true;
        } catch {
            await new Promise((resolve) => setTimeout(resolve, 5 * 1000));
        }
    }
};
